import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { Sl1ImportJobStatusList } from "shared/sl1ImportData"
import { sl1WindowLabel } from "./sl1WindowLabel"
import {
  Sl1ImportJobDto,
  useGetSl1ImportFleetQuery,
  useGetSl1ImportJobsQuery,
} from "src/features/things/sl1ImportApi"
import {
  Sl1JobStatusChip,
  fmtNum,
  fmtStamp,
  panelSx,
  sl1JobStatusColor,
} from "./Sl1ImportChrome"

/**
 * Screen 2 — every recon and apply, newest first (LOCKER-382 §D-4).
 *
 * Reads entirely from the job Thing: every count is denormalised onto the record,
 * so opening this page costs **no report reads at all** and the history stays
 * readable after an old report has been deleted or aged out. That is the whole
 * argument for `counts` living on the Thing rather than only in the report.
 *
 * Clicking a row opens the report detail. The three filters are client-side — the
 * estate is tens of runs, and a server-side filter would be a second place for the
 * vocabulary to live.
 */

const stripeSx = (color: string) => ({
  borderLeft: "3px solid",
  borderLeftColor: color,
})

const stripeFor = (status: Sl1ImportJobDto["status"]): string => {
  const c = sl1JobStatusColor(status)
  return c === "success"
    ? "success.main"
    : c === "error"
      ? "error.main"
      : c === "info"
        ? "info.main"
        : "divider"
}

export const Sl1ImportJobHistory: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const jobsQuery = useGetSl1ImportJobsQuery({ limit: 500 })
  const fleetQuery = useGetSl1ImportFleetQuery({ limit: 500 })

  const [provider, setProvider] = useState("all")
  const [kind, setKind] = useState<"all" | "recon" | "apply">("all")
  const [status, setStatus] = useState("all")

  /** Provider tenant → display name, so the table shows a name and not an id. */
  const providerNames = useMemo(() => {
    const m = new Map<string, string>()
    for (const f of fleetQuery.data?.things ?? []) {
      if (f.sl2TenantId) m.set(f.sl2TenantId, f.providerName)
    }
    return m
  }, [fleetQuery.data])

  const jobs = useMemo(() => {
    const all = jobsQuery.data?.things ?? []
    return [...all]
      .filter((j) => provider === "all" || j.providerTenantId === provider)
      .filter((j) => kind === "all" || j.kind === kind)
      .filter((j) => status === "all" || j.status === status)
      .sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1))
  }, [jobsQuery.data, provider, kind, status])

  const providerOptions = useMemo(
    () =>
      [...new Set((jobsQuery.data?.things ?? []).map((j) => j.providerTenantId))]
        .sort()
        .map((id) => ({ id, label: providerNames.get(id) ?? id })),
    [jobsQuery.data, providerNames],
  )

  if (jobsQuery.isLoading) {
    return <Typography variant="body2">{t("sl1LoadingJobs")}</Typography>
  }
  if (jobsQuery.error) {
    return <Alert severity="error">{t("sl1JobsLoadFailed")}</Alert>
  }

  // `label`, not `key` — same reason as the fleet table: gitleaks'
  // `generic-api-key` fires on `key: "<10+ chars>"`. See `.gitleaks.toml`.
  const headers: { label: string; align: "left" | "right" }[] = [
    { label: "sl1ColJob", align: "left" },
    { label: "sl1ColProvider", align: "left" },
    { label: "sl1ColKind", align: "left" },
    { label: "sl1ColWindow", align: "left" },
    { label: "sl1ColSl1Rows", align: "right" },
    { label: "sl1ColInSl2", align: "right" },
    { label: "sl1ColToWriteOrWritten", align: "right" },
    { label: "sl1ColAnomalies", align: "right" },
    { label: "sl1ColStatus", align: "left" },
    { label: "sl1ColRanBy", align: "left" },
  ]

  return (
    <Stack data-ui="UI-SL2W-172" spacing={2}>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label={t("sl1FilterProvider")}
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="all">{t("sl1FilterAll")}</MenuItem>
          {providerOptions.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t("sl1FilterKind")}
          value={kind}
          onChange={(e) => setKind(e.target.value as typeof kind)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">{t("sl1FilterAll")}</MenuItem>
          <MenuItem value="recon">{t("sl1KindRecon")}</MenuItem>
          <MenuItem value="apply">{t("sl1KindApply")}</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label={t("sl1FilterStatus")}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="all">{t("sl1FilterAll")}</MenuItem>
          {Sl1ImportJobStatusList.map((s) => (
            <MenuItem key={s} value={s}>
              {t(`sl1JobStatus_${s}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box sx={{ ...panelSx, overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              {headers.map((h) => (
                <TableCell
                  key={h.label}
                  align={h.align}
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.62rem",
                    letterSpacing: "0.09em",
                    textTransform: "uppercase",
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                    bgcolor: "action.hover",
                  }}
                >
                  {t(h.label)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((j) => {
              const anomalies = Object.values(j.anomalyCounts ?? {}).reduce(
                (a, b) => a + (b ?? 0),
                0,
              )
              return (
                <TableRow
                  key={j.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate(`/sa/sl1-import/${encodeURIComponent(j.jobId)}`)
                  }
                >
                  <TableCell sx={stripeSx(stripeFor(j.status))}>
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.76rem",
                        fontWeight: 600,
                        color: "primary.main",
                      }}
                    >
                      {j.jobId}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.disabled" }}
                    >
                      {/* An apply names the recon that authorised it — that join is
                          the audit chain, and hiding it makes a write look
                          unsourced. */}
                      {j.basedOnJobId
                        ? t("sl1FromRecon", { id: j.basedOnJobId })
                        : fmtStamp(j.startedAt).slice(11, 16)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography sx={{ fontSize: "0.8rem" }}>
                      {providerNames.get(j.providerTenantId) ??
                        j.providerTenantId}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.disabled", fontFamily: "monospace" }}
                    >
                      {j.providerTenantId}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t(j.kind === "apply" ? "sl1KindApply" : "sl1KindRecon")}
                      color={j.kind === "apply" ? "primary" : "default"}
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        letterSpacing: "0.07em",
                        textTransform: "uppercase",
                        height: 20,
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                    {sl1WindowLabel(j.window, t)}
                  </TableCell>

                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {fmtNum(j.counts?.found)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {fmtNum(
                      (j.counts?.matchedExact ?? 0) +
                        (j.counts?.matchedFallback ?? 0),
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: j.kind === "apply" ? 700 : 400,
                    }}
                  >
                    {/* An apply performed no classification, so `found`,
                        `matchedExact` and `unmatched` are all zero on its record
                        BY CONSTRUCTION — rendering them made every apply row read
                        `0 0 0` and say nothing about the thousands of rows it
                        wrote. This column carries what each kind actually did:
                        rows still to write for a recon, rows written for an
                        apply. */}
                    {j.kind === "apply"
                      ? fmtNum(j.appliedCount ?? j.counts?.imported)
                      : fmtNum(
                          (j.counts?.unmatched ?? 0) +
                            (j.counts?.replacesHusk ?? 0),
                        )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontVariantNumeric: "tabular-nums",
                      color: anomalies > 0 ? "info.main" : "text.disabled",
                      fontWeight: anomalies > 0 ? 700 : 400,
                    }}
                  >
                    {fmtNum(anomalies)}
                  </TableCell>

                  <TableCell>
                    <Sl1JobStatusChip status={j.status} kind={j.kind} />
                    {/* A PARTIAL apply says how far it got. That number is the only
                        thing that turns "it stopped" into a resumable job rather
                        than a mystery. */}
                    {j.status === "PARTIAL" &&
                      j.appliedCount !== undefined && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: "text.secondary",
                            fontFamily: "monospace",
                          }}
                        >
                          {j.appliedCount}/
                          {(j.counts?.unmatched ?? 0) +
                            (j.counts?.replacesHusk ?? 0)}
                        </Typography>
                      )}
                  </TableCell>

                  <TableCell sx={{ fontSize: "0.74rem", color: "text.secondary" }}>
                    {j.triggeredBy ?? "—"}
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "text.disabled",
                        fontFamily: "monospace",
                      }}
                    >
                      {j.gitCommit}
                    </Typography>
                  </TableCell>
                </TableRow>
              )
            })}

            {!jobs.length && (
              <TableRow>
                <TableCell
                  colSpan={headers.length}
                  sx={{ textAlign: "center", py: 5 }}
                >
                  <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                    {t("sl1NoJobsYet")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  )
}

export default Sl1ImportJobHistory
